import React, { useState, useEffect } from 'react';
import apiClient from '../api/client';
import DataTable from '../components/DataTable';
import FeedbackSubmitForm from '../components/FeedbackSubmitForm';
import { format } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Plus, Brain } from 'lucide-react';

const Feedback = () => {
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);

  useEffect(() => {
    fetchData();
  }, [filterType]);

  const fetchData = async () => {
    try {
      const params = filterType !== 'all' ? { feedback_type: filterType } : {};
      const [feedbackRes, analyticsRes] = await Promise.all([
        apiClient.get('/feedback', { params }),
        apiClient.get('/feedback/stats/analytics')
      ]);
      setFeedbackItems(feedbackRes.data);
      setAnalytics(analyticsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching feedback:', error);
      setLoading(false);
    }
  };

  const getTypeBadge = (type) => {
    const colors = {
      idea: 'bg-blue-100 text-blue-800',
      suggestion: 'bg-green-100 text-green-800',
      resistance: 'bg-yellow-100 text-yellow-800',
      complaint: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${colors[type] || 'bg-gray-100 text-gray-800'}`}>
        {type.toUpperCase()}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const colors = {
      submitted: 'bg-gray-100 text-gray-800',
      under_review: 'bg-blue-100 text-blue-800',
      implemented: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const columns = [
    { field: 'title', header: 'Title', sortable: true },
    { 
      field: 'feedback_type', 
      header: 'Type', 
      sortable: true,
      render: (value) => getTypeBadge(value)
    },
    { 
      field: 'status', 
      header: 'Status', 
      sortable: true,
      render: (value) => getStatusBadge(value)
    },
    { 
      field: 'submission_date', 
      header: 'Submitted', 
      sortable: true,
      render: (value) => format(new Date(value), 'MMM dd, yyyy')
    },
    { 
      field: 'submitted_by_id', 
      header: 'Submitted By', 
      sortable: true,
      render: (value) => value.substring(0, 8) + '...'
    }
  ];

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  // Prepare chart data
  const typeChartData = analytics?.by_type ? Object.entries(analytics.by_type).map(([key, value]) => ({
    name: key.charAt(0).toUpperCase() + key.slice(1),
    count: value
  })) : [];

  const statusChartData = analytics?.by_status ? Object.entries(analytics.by_status).map(([key, value]) => ({
    name: key.replace('_', ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
    count: value
  })) : [];

  const handleAIAnalysis = async () => {
    setLoadingAI(true);
    try {
      const response = await apiClient.post('/ai/analyze-feedback');
      setAiAnalysis(response.data.analysis);
    } catch (error) {
      console.error('Error getting AI analysis:', error);
      alert('Failed to get AI analysis');
    } finally {
      setLoadingAI(false);
    }
  };

  // Get a sample user ID from the first feedback item or use a default
  const sampleUserId = feedbackItems.length > 0 ? feedbackItems[0].submitted_by_id : 'user-001';

  return (
    <div className="space-y-6" data-testid="feedback-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Feedback Portal</h1>
          <p className="mt-2 text-gray-600">Staff improvement ideas and resistance reports</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowFeedbackForm(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            data-testid="submit-feedback-btn"
          >
            <Plus size={20} className="mr-2" />
            Submit Feedback
          </button>
          <button
            onClick={handleAIAnalysis}
            disabled={loadingAI}
            className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-purple-300"
          >
            <Brain size={20} className="mr-2" />
            {loadingAI ? 'Analyzing...' : 'AI Analysis'}
          </button>
        </div>
      </div>

      {/* AI Analysis */}
      {aiAnalysis && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">AI-Powered Feedback Analysis</h2>
          <p className="text-gray-800 whitespace-pre-wrap">{aiAnalysis}</p>
        </div>
      )}

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm font-medium text-gray-600">Total Feedback</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">{analytics?.total_feedback || 0}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm font-medium text-gray-600">Ideas</p>
          <p className="mt-2 text-3xl font-bold text-blue-600">{analytics?.by_type?.idea || 0}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm font-medium text-gray-600">Implemented</p>
          <p className="mt-2 text-3xl font-bold text-green-600">{analytics?.by_status?.implemented || 0}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm font-medium text-gray-600">Under Review</p>
          <p className="mt-2 text-3xl font-bold text-yellow-600">{analytics?.by_status?.under_review || 0}</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Feedback by Type</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={typeChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Feedback by Status</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={statusChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">Filter by Type:</span>
          {['all', 'idea', 'suggestion', 'resistance', 'complaint'].map((type) => (
            <button
              key={type}
              onClick={() => setFilterType(type)}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                filterType === type
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              data-testid={`filter-${type}`}
            >
              {type === 'all' ? 'All' : type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Feedback Table */}
      <DataTable columns={columns} data={feedbackItems} />

      {/* Modal */}
      {showFeedbackForm && (
        <FeedbackSubmitForm
          userId={sampleUserId}
          onClose={() => setShowFeedbackForm(false)}
          onSuccess={() => {
            setShowFeedbackForm(false);
            fetchData();
          }}
        />
      )}
    </div>
  );
};

export default Feedback;